import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  LogIn,
  LogOut,
} from "lucide-react";

const formatTime = (value) => {
  if (!value) return null;

  const [hours = "0", minutes = "0", seconds = "0"] = value.split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), Number(seconds), 0);

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrentTime = (value) =>
  value.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const getErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0)
    return detail[0].msg || fallback;
  return fallback;
};

const SimpleAttendanceCard = ({ variant = "card" }) => {
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());

  const loadTodayAttendance = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get("/v1/attendance/today");
      setAttendance(response.data || null);
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't load today's attendance."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTodayAttendance();
  }, [loadTodayAttendance]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  };

  const handleCheckIn = async () => {
    setSubmitting(true);
    setError("");
    setMessage("");

    try {
      const location = await getCurrentLocation();

      const response = await axios.post("/v1/attendance/checkin", {
        latitude: location.latitude,
        longitude: location.longitude,
      });

      setAttendance(response.data.attendance);
      setMessage(response.data.message);
    } catch (err) {
      if (err.code === 1) {
        setError("Location permission denied.");
      } else if (err.code === 2) {
        setError("Unable to determine your location.");
      } else if (err.code === 3) {
        setError("Location request timed out.");
      } else {
        setError(getErrorMessage(err, "Check in failed."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckOut = async () => {
    setSubmitting(true);
    setError("");
    setMessage("");

    try {
      const location = await getCurrentLocation();

      const response = await axios.post("/v1/attendance/checkout", {
        latitude: location.latitude,
        longitude: location.longitude,
      });

      setAttendance(response.data.attendance);
      setMessage(response.data.message);
    } catch (err) {
      if (err.code === 1) {
        setError("Location permission denied.");
      } else if (err.code === 2) {
        setError("Unable to determine your location.");
      } else if (err.code === 3) {
        setError("Location request timed out.");
      } else {
        setError(getErrorMessage(err, "Check out failed."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hasCheckedIn = Boolean(attendance?.check_in_time);
  const hasCheckedOut = Boolean(attendance?.check_out_time);

  if (variant === "quick-action") {
    const buttonBaseClass =
      "w-full inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors";

    return (
      <div
        className={`flex flex-col justify-center p-5 bg-white border-2 rounded-xl min-h-[138px] ${
          hasCheckedOut
            ? "border-gray-200"
            : hasCheckedIn
              ? "border-blue-200"
              : "border-green-200"
        }`}
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center text-gray-600">
            <Loader2 className="w-8 h-8 mb-2 animate-spin" />
            <span className="text-sm font-medium">Loading</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center mb-3">
              {hasCheckedOut ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : hasCheckedIn ? (
                <LogOut className="w-8 h-8 text-blue-600" />
              ) : (
                <LogIn className="w-8 h-8 text-green-600" />
              )}
            </div>

            <div className="text-center mb-3 min-h-[38px]">
              {!hasCheckedIn && (
                <>
                  <p className="text-xs text-gray-500">Current Time</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrentTime(currentTime)}
                  </p>
                </>
              )}

              {hasCheckedIn && !hasCheckedOut && (
                <>
                  <p className="text-xs text-gray-500">Checked In</p>
                  <p className="text-sm font-semibold text-blue-700">
                    {formatTime(attendance.check_in_time)}
                  </p>
                </>
              )}

              {hasCheckedOut && (
                <>
                  <p className="text-xs text-gray-500">Total Hours</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {Number(attendance.total_hours || 0).toFixed(2)}
                  </p>
                </>
              )}
            </div>

            {error && (
              <p className="mb-2 text-xs text-red-600 text-center">{error}</p>
            )}

            {!hasCheckedIn && (
              <button
                type="button"
                onClick={handleCheckIn}
                disabled={submitting}
                className={`${buttonBaseClass} bg-green-600 text-white hover:bg-green-700`}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <LogIn className="w-4 h-4 mr-2" />
                )}
                Check In
              </button>
            )}

            {hasCheckedIn && !hasCheckedOut && (
              <button
                type="button"
                onClick={handleCheckOut}
                disabled={submitting}
                className={`${buttonBaseClass} bg-blue-600 text-white hover:bg-blue-700`}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <LogOut className="w-4 h-4 mr-2" />
                )}
                Check Out
              </button>
            )}

            {hasCheckedOut && (
              <button
                type="button"
                disabled
                className={`${buttonBaseClass} bg-gray-100 text-gray-700`}
              >
                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                Completed
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center">
          <Clock className="w-6 h-6 text-green-600 mr-2" />
          <h3 className="text-xl font-semibold text-gray-900">
            Today Attendance
          </h3>
        </div>
        {hasCheckedOut ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        ) : (
          <span className="text-sm font-semibold text-gray-700">
            {formatCurrentTime(currentTime)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center text-gray-600 py-6">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Loading attendance
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-5">
            {hasCheckedIn && (
              <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-4 py-3">
                <span className="text-sm font-medium text-gray-700">
                  Checked In
                </span>
                <span className="text-sm font-semibold text-green-700">
                  {formatTime(attendance.check_in_time)}
                </span>
              </div>
            )}

            {hasCheckedOut && (
              <>
                <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                  <span className="text-sm font-medium text-gray-700">
                    Checked Out
                  </span>
                  <span className="text-sm font-semibold text-blue-700">
                    {formatTime(attendance.check_out_time)}
                  </span>
                </div>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                  <span className="text-sm font-medium text-gray-700">
                    Total Hours
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {Number(attendance.total_hours || 0).toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>

          {error && (
            <div className="flex items-start mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {message && !error && (
            <div className="flex items-start mb-4 p-3 bg-green-50 border border-green-100 rounded-lg text-sm text-green-700">
              <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              {message}
            </div>
          )}

          {!hasCheckedIn && (
            <button
              type="button"
              onClick={handleCheckIn}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <LogIn className="w-5 h-5 mr-2" />
              )}
              Check In
            </button>
          )}

          {hasCheckedIn && !hasCheckedOut && (
            <button
              type="button"
              onClick={handleCheckOut}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <LogOut className="w-5 h-5 mr-2" />
              )}
              Check Out
            </button>
          )}

          {hasCheckedOut && (
            <button
              type="button"
              disabled
              className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gray-100 text-gray-700 font-medium cursor-not-allowed"
            >
              <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
              Attendance Completed
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default SimpleAttendanceCard;
